import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const AuthContext = createContext(null);
const SESSION_KEY = 'rwm_token';
const REMEMBER_KEY = 'rwm_remember_token';

function getRememberToken() {
  try {
    return localStorage.getItem(REMEMBER_KEY);
  } catch {
    return null;
  }
}

function setRememberToken(token) {
  try {
    localStorage.setItem(REMEMBER_KEY, token);
  } catch {
    // ignore
  }
}

function clearRememberToken() {
  try {
    localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // ignore
  }
}

export function dashboardPathForRole(role) {
  if (role === 'company') return '/company/dashboard';
  if (role === 'admin') return '/admin';
  return '/dashboard';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) ?? getRememberToken() ?? null;
    } catch {
      return getRememberToken() ?? null;
    }
  });
  const [authLoading, setAuthLoading] = useState(true);
  const broadcastRef = useRef(null);

  const setToken = useCallback((newToken) => {
    setTokenState(newToken);
    try {
      if (newToken) {
        sessionStorage.setItem(SESSION_KEY, newToken);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      // sessionStorage unavailable — silent fail
    }
  }, []);

  const apiBase = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/$/, ''), []);

  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const refreshUser = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!apiBase || !currentToken) {
      return null;
    }

    const maxAttempts = 3;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const res = await fetch(`${apiBase}/api/v1/auth/me`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${currentToken}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          const u = data?.data ?? null;
          setUser(u);
          return u;
        }
        if (res.status >= 500 && attempt < maxAttempts - 1) {
          await new Promise((resolve) => {
            setTimeout(resolve, 400);
          });
          continue;
        }
        setUser(null);
        setToken(null);
        return null;
      } catch {
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => {
            setTimeout(resolve, 400);
          });
          continue;
        }
        setUser(null);
        setToken(null);
        return null;
      }
    }

    return null;
  }, [apiBase, setToken]);

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('rwm-auth');
      broadcastRef.current = bc;
      bc.addEventListener('message', (e) => {
        if (e.data === 'logout') {
          setUser(null);
          setToken(null);
          clearRememberToken();
        }
      });
    } catch {
      // BroadcastChannel unsupported
    }
    return () => {
      bc?.close();
      broadcastRef.current = null;
    };
  }, [setToken]);

  useEffect(() => {
    if (!apiBase) {
      setAuthLoading(false);
      return undefined;
    }
    if (!token) {
      setAuthLoading(false);
      return undefined;
    }

    let cancelled = false;

    refreshUser().finally(() => {
      if (!cancelled) setAuthLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiBase, token, refreshUser]);

  const login = useCallback(
    (userData, authToken, remember = false) => {
      setUser(userData);
      setToken(authToken ?? null);
      if (remember && authToken) {
        setRememberToken(authToken);
      }
    },
    [setToken]
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearRememberToken();
    try {
      broadcastRef.current?.postMessage('logout');
    } catch {
      // BroadcastChannel unsupported — ignore
    }
  }, [setToken]);

  const isVerified = Boolean(user?.email_verified_at);

  const value = useMemo(
    () => ({ user, token, authLoading, login, logout, apiBase, refreshUser, isVerified }),
    [user, token, authLoading, login, logout, apiBase, refreshUser, isVerified]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
